import React, { useState } from "react";
import { toast } from "react-toastify";
import Modal from "@mui/material/Modal";
import Popover from "@mui/material/Popover";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import { ThemeProvider } from "@mui/material/styles";
import violetTheme from "../../../theme/rightworkTheme";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { postRightworkAPI } from "../requests/utilities/requests";
import {
  API_update_board_card,
  API_set_card_label,
  API_set_card_member,
  API_create_board_label,
  API_delete_board_label,
} from "../../../constants";
import { LABEL_COLOR_CLASSES } from "./BoardCardTile";
import BoardCardAttachments from "./BoardCardAttachments";
import BoardCardChecklists from "./BoardCardChecklists";
import BoardCardComments from "./BoardCardComments";

// The colors available for board labels — derived from the shared color map
// so this list can't drift from BoardLabel.COLOR_CHOICES.
const LABEL_COLOR_OPTIONS = Object.keys(LABEL_COLOR_CLASSES);

// MUI theming/styling shared with the app's other modals (see MaintenanceTicketDrawer).

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "8px",
    "&.Mui-focused fieldset": { borderColor: "#6d28d9" },
  },
};

// due_date is a plain calendar date; convert between the API's "YYYY-MM-DD"
// string and the Date object the MUI DatePicker uses, with no timezone shift.
function parseYMD(value) {
  if (!value) return null;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function toYMD(date) {
  if (!date || isNaN(date.getTime())) return "";
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${m}-${d}`;
}

const BoardCardModal = ({
  card,
  listName,
  storeId,
  companyId,
  boardLabels,
  boardMembers,
  onClose,
  onCardUpdated,
  onArchive,
  onDuplicate,
  onLabelCreated,
  onBoardNeedsRefresh,
}) => {
  const [titleDraft, setTitleDraft] = useState(card.title);
  const [descDraft, setDescDraft] = useState(card.description || "");
  const [labelAnchor, setLabelAnchor] = useState(null);
  const [memberAnchor, setMemberAnchor] = useState(null);
  const [newLabelName, setNewLabelName] = useState("");
  const [newLabelColor, setNewLabelColor] = useState("green");

  const cardLabels = card.labels || [];
  const cardMembers = card.members || [];
  const descChanged = descDraft !== (card.description || "");
  const dueDateValue = parseYMD(card.due_date);

  /* ----- API helpers ----- */

  // All edits update the board optimistically, then reconcile with the
  // server response — or revert to the pre-edit card on failure.
  const patchCard = async (fields) => {
    onCardUpdated({ ...card, ...fields });
    try {
      const res = await postRightworkAPI(API_update_board_card, {
        store_id: storeId,
        board_card_uuid: card.uuid,
        ...fields,
      });
      const updated = res.data?.redux?.payload?.board_card;
      if (updated) onCardUpdated(updated);
    } catch (error) {
      toast.error("Failed to update card");
      onCardUpdated(card);
    }
  };

  const saveTitle = () => {
    const trimmed = titleDraft.trim();
    if (!trimmed || trimmed === card.title) {
      setTitleDraft(card.title);
      return;
    }
    patchCard({ title: trimmed });
  };

  const saveDescription = () => {
    if (!descChanged) return;
    patchCard({ description: descDraft });
  };

  const toggleLabel = async (label) => {
    const has = cardLabels.some((l) => l.uuid === label.uuid);
    onCardUpdated({
      ...card,
      labels: has
        ? cardLabels.filter((l) => l.uuid !== label.uuid)
        : [...cardLabels, label],
    });
    try {
      const res = await postRightworkAPI(API_set_card_label, {
        store_id: storeId,
        board_card_uuid: card.uuid,
        board_label_uuid: label.uuid,
        action: has ? "remove" : "add",
      });
      const updated = res.data?.redux?.payload?.board_card;
      if (updated) onCardUpdated(updated);
    } catch (error) {
      toast.error("Failed to update label");
      onCardUpdated(card);
    }
  };

  const createLabel = async () => {
    try {
      const res = await postRightworkAPI(API_create_board_label, {
        store_id: storeId,
        name: newLabelName.trim(),
        color: newLabelColor,
      });
      const label = res.data?.redux?.payload?.board_label;
      if (label) {
        onLabelCreated(label);
        setNewLabelName("");
        // Auto-apply the freshly created label to this card.
        toggleLabel(label);
      }
    } catch (error) {
      toast.error("Failed to create label");
    }
  };

  const deleteLabel = async (label) => {
    try {
      await postRightworkAPI(API_delete_board_label, {
        store_id: storeId,
        board_label_uuid: label.uuid,
      });
      // Deleting a label detaches it from every card, so resync the board.
      onBoardNeedsRefresh();
    } catch (error) {
      toast.error("Failed to delete label");
    }
  };

  const toggleMember = async (member) => {
    const has = cardMembers.some((m) => m.id === member.id);
    onCardUpdated({
      ...card,
      members: has
        ? cardMembers.filter((m) => m.id !== member.id)
        : [...cardMembers, member],
    });
    try {
      const res = await postRightworkAPI(API_set_card_member, {
        store_id: storeId,
        board_card_uuid: card.uuid,
        store_team_member_id: member.id,
        action: has ? "remove" : "add",
      });
      const updated = res.data?.redux?.payload?.board_card;
      if (updated) onCardUpdated(updated);
    } catch (error) {
      toast.error("Failed to update member");
      onCardUpdated(card);
    }
  };

  /* ----- Render ----- */

  return (
    <Modal open onClose={onClose}>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-white rounded-xl shadow-xl outline-none max-h-[85vh] overflow-y-auto">
        {/* Header — pinned */}
        <div className="sticky top-0 z-10 bg-white flex items-start justify-between gap-3 px-6 pt-5 pb-3 border-b border-slate-200">
          <div className="flex-1">
            <TextField
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.target.blur();
                }
              }}
              fullWidth
              variant="outlined"
              size="small"
              inputProps={{ maxLength: 255 }}
              sx={{
                ...textFieldSx,
                "& .MuiOutlinedInput-input": { fontSize: "1.05rem", fontWeight: 600 },
              }}
            />
            {listName && (
              <p className="text-xs text-slate-400 px-0.5 mt-1">In {listName}</p>
            )}
          </div>
          <button
            className="text-slate-400 hover:text-slate-700 mt-1"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body — scrolls */}
        <div className="px-6 py-4 space-y-5 min-h-[50vh]">
          {/* Labels + Members + Due date controls */}
          <div className="flex flex-wrap gap-2">
            <button
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
              onClick={(e) => setLabelAnchor(e.currentTarget)}
            >
              <LabelOutlinedIcon style={{ fontSize: 16 }} />
              Labels
            </button>
            <button
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
              onClick={(e) => setMemberAnchor(e.currentTarget)}
            >
              <PersonOutlineIcon style={{ fontSize: 16 }} />
              Members
            </button>
          </div>

          {/* Current labels */}
          {cardLabels.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-1.5">LABELS</p>
              <div className="flex flex-wrap gap-1.5">
                {cardLabels.map((label) => (
                  <span
                    key={label.uuid}
                    className={`text-xs font-medium text-white px-2.5 py-1 rounded ${
                      LABEL_COLOR_CLASSES[label.color] || "bg-slate-400"
                    }`}
                  >
                    {label.name || " "}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Current members */}
          {cardMembers.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-1.5">MEMBERS</p>
              <div className="flex flex-wrap gap-1.5">
                {cardMembers.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center gap-1.5 bg-slate-100 rounded-full pl-1 pr-2.5 py-1"
                  >
                    <div className="h-6 w-6 rounded-full bg-violet-100 flex items-center justify-center">
                      <span className="text-[10px] font-semibold text-violet-700">
                        {m.initials}
                      </span>
                    </div>
                    <span className="text-xs text-slate-700">{m.full_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Due date */}
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-1.5">DUE DATE</p>
            <div className="flex items-center gap-3">
              <ThemeProvider theme={violetTheme}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={dueDateValue}
                    onChange={(newValue) => {
                      if (newValue === null) {
                        patchCard({ due_date: "" });
                      } else if (newValue instanceof Date && !isNaN(newValue.getTime())) {
                        patchCard({ due_date: toYMD(newValue) });
                      }
                      // Ignore invalid intermediate input from typing.
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        placeholder: "Select due date…",
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                            borderRadius: "8px",
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6d28d9",
                            },
                          },
                        },
                      },
                      actionBar: { actions: ["clear", "today"] },
                    }}
                  />
                </LocalizationProvider>
              </ThemeProvider>
              {card.due_date && (
                <label className="flex items-center text-sm text-slate-600">
                  <Checkbox
                    checked={!!card.due_complete}
                    onChange={(e) => patchCard({ due_complete: e.target.checked })}
                    size="small"
                    sx={{
                      color: "#94a3b8",
                      padding: "4px",
                      "&.Mui-checked": { color: "#6d28d9" },
                    }}
                  />
                  Complete
                </label>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-1.5">DESCRIPTION</p>
            <TextField
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              onBlur={saveDescription}
              placeholder="Add a more detailed description…"
              fullWidth
              variant="outlined"
              size="small"
              multiline
              minRows={4}
              sx={textFieldSx}
            />
            {descChanged && (
              <div className="flex items-center gap-2 mt-1.5">
                <button
                  className="px-3 py-1.5 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium"
                  onClick={saveDescription}
                >
                  Save
                </button>
                <span className="text-xs text-slate-400">
                  or click outside to save
                </span>
              </div>
            )}
          </div>

          {/* Checklists */}
          <BoardCardChecklists
            card={card}
            storeId={storeId}
            onCardUpdated={onCardUpdated}
          />

          {/* Attachments */}
          <BoardCardAttachments
            card={card}
            storeId={storeId}
            companyId={companyId}
            onCardUpdated={onCardUpdated}
          />

          {/* Comments */}
          <BoardCardComments
            storeId={storeId}
            cardUuid={card.uuid}
            onCommentPosted={() =>
              onCardUpdated({
                ...card,
                comment_count: (card.comment_count || 0) + 1,
              })
            }
          />

        </div>

        {/* Footer — pinned card actions */}
        <div className="sticky bottom-0 z-10 bg-white px-6 py-3 border-t border-slate-200 flex flex-wrap gap-2">
          <button
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100"
            onClick={onDuplicate}
          >
            <ContentCopyIcon style={{ fontSize: 17 }} />
            Copy card
          </button>
          <button
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50"
            onClick={onArchive}
          >
            <DeleteOutlineIcon style={{ fontSize: 18 }} />
            Archive card
          </button>
        </div>

        {/* Labels popover */}
        <Popover
          open={Boolean(labelAnchor)}
          anchorEl={labelAnchor}
          onClose={() => setLabelAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <div className="p-3 w-72">
            <p className="text-xs font-semibold text-slate-500 mb-2">LABELS</p>
            {(boardLabels || []).map((label) => {
              const has = cardLabels.some((l) => l.uuid === label.uuid);
              return (
                <div key={label.uuid} className="flex items-center gap-1.5 mb-1.5">
                  <button
                    className={`flex-1 flex items-center justify-between px-2.5 h-8 rounded text-white text-xs font-medium ${
                      LABEL_COLOR_CLASSES[label.color] || "bg-slate-400"
                    }`}
                    onClick={() => toggleLabel(label)}
                  >
                    <span className="truncate">{label.name || " "}</span>
                    {has && <CheckIcon style={{ fontSize: 16 }} />}
                  </button>
                  <button
                    className="text-slate-400 hover:text-red-600"
                    onClick={() => deleteLabel(label)}
                    title="Delete label"
                  >
                    <DeleteOutlineIcon style={{ fontSize: 18 }} />
                  </button>
                </div>
              );
            })}
            {(boardLabels || []).length === 0 && (
              <p className="text-xs text-slate-400 mb-2">No labels yet.</p>
            )}

            {/* Create label */}
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-xs font-semibold text-slate-500 mb-1.5">
                CREATE A LABEL
              </p>
              <TextField
                value={newLabelName}
                onChange={(e) => setNewLabelName(e.target.value)}
                placeholder="Label name (optional)"
                fullWidth
                variant="outlined"
                size="small"
                inputProps={{ maxLength: 100 }}
                sx={textFieldSx}
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {LABEL_COLOR_OPTIONS.map((color) => (
                  <button
                    key={color}
                    onClick={() => setNewLabelColor(color)}
                    className={`h-6 w-6 rounded ${LABEL_COLOR_CLASSES[color]} ${
                      newLabelColor === color
                        ? "ring-2 ring-offset-1 ring-slate-500"
                        : ""
                    }`}
                  />
                ))}
              </div>
              <button
                className="flex items-center gap-1 mt-2.5 px-3 py-1.5 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium"
                onClick={createLabel}
              >
                <AddIcon style={{ fontSize: 16 }} />
                Create
              </button>
            </div>
          </div>
        </Popover>

        {/* Members popover */}
        <Popover
          open={Boolean(memberAnchor)}
          anchorEl={memberAnchor}
          onClose={() => setMemberAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <div className="p-3 w-64">
            <p className="text-xs font-semibold text-slate-500 mb-2">MEMBERS</p>
            {(boardMembers || []).map((member) => {
              const has = cardMembers.some((m) => m.id === member.id);
              return (
                <button
                  key={member.id}
                  className="flex items-center gap-2 w-full px-2 py-1.5 rounded hover:bg-slate-100"
                  onClick={() => toggleMember(member)}
                >
                  <div className="h-7 w-7 rounded-full bg-violet-100 flex items-center justify-center">
                    <span className="text-[11px] font-semibold text-violet-700">
                      {member.initials}
                    </span>
                  </div>
                  <span className="flex-1 text-sm text-slate-700 text-left truncate">
                    {member.full_name}
                  </span>
                  {has && (
                    <CheckIcon style={{ fontSize: 16 }} className="text-violet-700" />
                  )}
                </button>
              );
            })}
            {(boardMembers || []).length === 0 && (
              <p className="text-xs text-slate-400">No assignable members.</p>
            )}
          </div>
        </Popover>
      </div>
    </Modal>
  );
};

export default BoardCardModal;
